import React, { useState, useRef, useLayoutEffect } from "react";
import { fmtCurrency } from "../lib/formatters";
import YearExpensesPopover from "../components/YearExpensesPopover";

// ---------------------------------------------------------------------------
// Screen 4 — Reserve Account Matrix
//
// 2026-08-03 redesign per Dave:
//   * Year columns always the same width as a 30-year layout (i.e. each
//     panel always reserves 15 year slots — unused slots render blank so
//     column widths never stretch with horizon changes).
//   * Total column pinned to the far right.
//   * Horizon 5 / 10 / 15 → top panel only; bottom panel hidden.
//   * Horizon 20 / 25 / 30 → top panel always shows the first 15 years
//     with a 15-year total; bottom panel shows the remainder with the
//     full-horizon (20 / 25 / 30) cumulative total pinned right.
// ---------------------------------------------------------------------------

const NAVY = "#1C4E80";
const SKY = "#3A87C4";
const PEAK_C = SKY;
const LOW_C = "#4A4A4A";
const sides = (c, w, bottom, top) =>
  `inset ${w}px 0 0 ${c}, inset -${w}px 0 0 ${c}` +
  (bottom ? `, inset 0 -${w}px 0 ${c}` : "") + (top ? `, inset 0 ${w}px 0 ${c}` : "");
// Peak = sky frame, Low Balance = red frame; both → sky outside red. Header adds top, last row bottom.
function markSides(year, peakYear, lowYear, bottom = false, top = false) {
  const isPeak = peakYear && year === peakYear.year;
  const isLow = lowYear && year === lowYear.year;
  if (isPeak && isLow) return `${sides(PEAK_C, 2, bottom, top)}, ${sides(LOW_C, 4, bottom, top)}`;
  if (isPeak) return sides(PEAK_C, 2, bottom, top);
  if (isLow) return sides(LOW_C, 2, bottom, top);
  return undefined;
}
const ROW_ALT = "#F5F8FC";

const SLOTS_PER_PANEL = 15;    // reserved year cells per panel — never varies
const LABEL_COL_W = 170; // fits "Fund Surplus (Deficit)" at 12px
// 15 year columns + the total column share the remaining width equally. Font is solved so the
// widest total value "($1,234,567)" (12 mono chars) fits: width = 12 * MONO_EM * font + 2 * CELL_PAD.
const DATA_COLS = 16;
const CELL_PAD = 5;
const MONO_EM = 0.61;
const MIN_FONT = 8.5;
const MAX_FONT = 12.5;
const MIN_COL_W = 12 * MONO_EM * MIN_FONT + 2 * CELL_PAD;

// Base metric rows. Two extra rows ("Fund Target at nn%" and
// "Fund Surplus (Deficit)") are appended dynamically in the screen
// component because the target percentage lives in the currently-loaded
// scenario variables. Dave 2026-02-08.
const CORE_METRIC_ROWS = [
  { key: "beginning",           label: "Beginning Balance", balance: true },
  { key: "contribution",        label: "Contributions" },
  { key: "interest",            label: "Interest" },
  { key: "expenditures",        label: "Expenditures", expense: true },
  // 2026-02-23 (Dave) — Break out Expenditures into its funding sources.
  // "From Reserves" is calculated (max(0, expenditures − SA)); Special
  // Assessments is the same top-level row as before, now visually
  // indented so both children read as an allocation of Expenditures.
  { key: "from_reserves",       label: "From Reserves", expense: true, indent: true },
  { key: "special_assessment",  label: "Special Assessments", indent: true, special: true },
  { key: "ending",              label: "Ending Balance", balance: true },
];

function fmtCell({ value, balance, expense, ramp, isTotal, surplus, special }) {
  const isZero = !value || Math.abs(value) < 0.5;
  if (isZero) return <span className="text-slate">—</span>;

  if (expense) {
    const abs = Math.abs(value);
    const inner = abs.toLocaleString(undefined, { maximumFractionDigits: 0 });
    return (
      <span style={{ color: "#000" }}>
        -{isTotal ? `$${inner}` : inner}
      </span>
    );
  }
  // Special Assessments — render in black (matches the neighbouring
  // Expenditures / From Reserves rows) rather than coral.
  if (special) {
    const formatted = fmtCurrency(Math.round(value));
    const text = isTotal ? formatted : formatted.replace("$", "");
    return <span style={{ color: "#000" }}>{text}</span>;
  }
  // Fund Surplus (Deficit) — both positive AND negative render in navy
  // (matching the row label + Fund Target row values). Negative values
  // remain parenthesized but no longer get a distinct color.
  if (surplus) {
    const abs = Math.abs(value);
    const inner = abs.toLocaleString(undefined, { maximumFractionDigits: 0 });
    if (value < 0) {
      return (
        <span style={{ color: NAVY, fontWeight: 600 }}>
          -{isTotal ? `$${inner}` : inner}
        </span>
      );
    }
    return (
      <span style={{ color: NAVY, fontWeight: 600 }}>
        {isTotal ? `$${inner}` : inner}
      </span>
    );
  }
  const formatted = fmtCurrency(Math.round(value));
  const noDollar = !balance && !isTotal;
  const text = noDollar ? formatted.replace("$", "") : formatted;
  const color = ramp ? SKY : undefined;
  const weight = balance ? 600 : 400;
  return <span style={{ color, fontWeight: weight }}>{text}</span>;
}

// Range totals come from the server's per-horizon summary (calc.display.horizons[N]).
function rangeTotals(summary) {
  if (!summary || !summary.horizon) return {};
  return {
    beginning: summary.beginning,
    ending: summary.ending,
    contribution: summary.total_contributions,
    interest: summary.total_interest,
    expenditures: summary.total_expenditures,
    from_reserves: summary.total_from_reserves,
    special_assessment: summary.total_special_assessments,
    // Fund Target / Surplus rollup — LAST year's snapshot (cumulative balances).
    fund_target: summary.target_balance,
    fund_surplus: summary.surplus,
  };
}

// One 15-slot panel. `rows` is the actual data (0–15 rows); the missing
// slots are padded with null so column widths stay identical across horizons.
// Measures the panel and returns the shared data-column width + the font that fits 7-figure values.
function useColMetrics(ref) {
  const [m, setM] = useState({ colW: MIN_COL_W, font: MIN_FONT });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => {
      const colW = Math.max(MIN_COL_W, (el.clientWidth - LABEL_COL_W) / DATA_COLS);
      const font = Math.min(MAX_FONT, Math.max(MIN_FONT, (colW - 2 * CELL_PAD) / (12 * MONO_EM)));
      setM({ colW, font: Math.floor(font * 4) / 4 });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return m;
}

function YearBlock({ rows, metricRows, range, rangeSummary, peakYear, lowYear, onExpenseClick, testid, planYear }) {
  const totals = rangeTotals(rangeSummary);
  const padCount = Math.max(0, SLOTS_PER_PANEL - rows.length);
  const slots = [...rows, ...Array(padCount).fill(null)];
  const wrapRef = useRef(null);
  const { colW, font } = useColMetrics(wrapRef);

  return (
    <div
      ref={wrapRef}
      style={{
        position: "relative",
        // 2026-02-28 (Dave, v2) — minWidth grew from 1240 → 1270 to
        // absorb the first-column widening (138 → 168) without
        // stealing pixels from the 15 year-slot columns.  Panel
        // still slots inside the radiused container on 1440-wide
        // viewports with comfortable side-padding.
        // 2026-10-08 (Dave) — Reverted a short-lived bump to 1600
        // that caused horizontal overflow on 1440-class monitors.
        // Full-bleed CSS still lets the panel stretch to the full
        // viewport on wider screens, and `tableLayout: fixed +
        // w-full` scales columns to whatever width the panel has.
        minWidth: LABEL_COL_W + DATA_COLS * MIN_COL_W,
        // 2026-02-28 (Dave) — tighter 3-px corner radius (was 8) so
        // the panels match the rest of the app's button + card
        // language.  `overflow: hidden` clips the header/totals cells
        // at the rounded corner regardless of their background paint.
        borderRadius: 3,
        overflow: "hidden",
        border: `1px solid ${NAVY}22`,
      }}
      data-testid={`${testid}-wrap`}
    >
    <table
      className="w-full text-[11.5px] font-mono tabular-nums border-separate"
      style={{
        tableLayout: "fixed",
        borderSpacing: 0,
      }}
      data-testid={testid}
    >
      <colgroup>
        {/* 2026-02-28 (Dave) — first-column label width trimmed 175 → 138
            so the cumulative-total column on the right can grow enough
            to fit "$n,nnn,nnn" and "yyyy-yyyy" without being clipped.
            2026-02-28 (Dave, v2) — bumped 138 → 168 so long labels
            like "Fund Target at 40%" fit on one line without the
            "40%" wrapping or being clipped by the sticky-cell edge. */}
        <col style={{ width: LABEL_COL_W }} />
        {/* Peak column gets a minimum width so 7-figure bracketed values fit inside the coral frame. */}
        {slots.map((r, i) => (
          <col key={i} style={{ width: colW }} />
        ))}
        {/* Cumulative-total column.  2026-02-08 Dave dialled this to 84 px
            so the nested navy separator sits inside the cell.
            2026-02-28 Dave grew it back to 120 px because 84 was clipping
            "$1,896,218" and "2026-2055" on wider horizons.  The nested
            `<div>` still owns the 3 px inset margin + 1 px separator so
            the visual weight of the divider is preserved. */}
        <col style={{ width: colW }} />
      </colgroup>
      <thead>
        <tr>
          <th
            className="text-left"
            style={{
              background: NAVY, color: "#fff",
              padding: "6px 12px",
              fontSize: 10.5, letterSpacing: "0.14em", fontWeight: 700,
              textTransform: "uppercase",
            }}
          >
            {/* 2026-10-08 (Dave) — plan-year context moved up into
                the ControlsStrip hero. This cell goes back to the
                compact "Reserve Account" row label. */}
            Reserve Account
          </th>
          {slots.map((r, i) => {
            return (
              <th
                key={i}
                className="text-right"
                style={{
                  background: NAVY,
                  color: "#fff",
                  padding: `6px ${CELL_PAD}px`,
                  fontWeight: 600,
                  fontSize: Math.min(11.5, font + 0.5),
                  boxShadow: r ? markSides(r.year, peakYear, lowYear, false, true) : undefined,
                }}
                data-testid={r ? `col-year-${r.year}` : `col-empty-${i}`}
              >
                {r ? r.year : ""}
              </th>
            );
          })}
          <th
            className="text-right"
            style={{
              background: NAVY, color: "#fff",
              padding: 0,
              fontWeight: 700, fontSize: Math.min(11.5, font + 0.5),
              whiteSpace: "nowrap",
            }}
          >
            <div
              style={{
                padding: `6px ${CELL_PAD}px`,
              }}
            >
              {range}
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        {metricRows.map((m, rowIdx) => {
          const isEnding = m.key === "ending";
          const isFundTarget = m.key === "fund_target";
          const isFundSurplus = m.key === "fund_surplus";
          const isTargetOrSurplus = isFundTarget || isFundSurplus;
          const bg = rowIdx % 2 === 1 ? ROW_ALT : "#fff";
          return (
            <tr key={m.key} data-testid={`metric-row-${m.key}`}>
              <td
                style={{
                  background: bg,
                  /* 2026-02-16 (Dave, rev 2) — the deletion of the
                     "Reserve Account Matrix" title bar bought back
                     ~40 px of vertical room, so tbody vertical padding
                     is dialled from 3 → 5 px (thead 4 → 6). Rows breathe
                     more without pushing the second 15-year block below
                     the fold at 1440×900. Text stays at fontSize 12/11.
                     2026-02-28 (Dave) — dialled BACK to 3 px each side
                     so both panels + the Fund Surplus (Deficit) row
                     land inside the viewport at 1440×900 without a
                     vertical scrollbar (screenshot 2026-02-28). */
                  padding: "3px 12px",
                  paddingLeft: m.indent ? 28 : 12,
                  fontFamily: "'Source Sans Pro', ui-sans-serif, system-ui",
                  fontSize: 11,
                  color: NAVY,
                  fontWeight: isEnding || isTargetOrSurplus ? 700 : 500,
                  // Top-border above Ending (as before) AND above Fund
                  // Target so the two new rows read as a "footer block"
                  // hanging off the Ending Balance row.
                  borderTop:
                    isEnding || isFundTarget ? `1px solid ${NAVY}22` : undefined,
                }}
              >
                {m.label}
              </td>
              {slots.map((r, i) => {
                if (!r) {
                  // Empty slot — keep alternating background, no content.
                  return (
                    <td
                      key={i}
                      className="text-right"
                      style={{ background: bg, padding: `3px ${CELL_PAD}px` }}
                    />
                  );
                }
                const isRamp = m.key === "contribution" && r.is_ramp_year;
                // Expenditures cells are click-to-drill when the year has
                // any spending; a subtle underline+pointer affordance on
                // hover signals the interaction.
                const isExpenseClickable =
                  m.key === "expenditures" &&
                  Math.abs(r.expenditures || 0) > 0.5 &&
                  typeof onExpenseClick === "function";
                return (
                  <td
                    key={i}
                    className={
                      "text-right " +
                      (isExpenseClickable
                        ? "cursor-pointer hover:underline hover:decoration-coral hover:decoration-1"
                        : "")
                    }
                    style={{
                      background: bg,
                      // 2026-10-08 (Dave) — year-cell horizontal
                      // padding bumped 6 → 7 so 7-figure 2056 values
                      // ($3,499,964, $2,586,610, $2,159,197) have
                      // modest breathing room without changing the
                      // table's overall width.
                      padding: `3px ${CELL_PAD}px`,
                      whiteSpace: "nowrap",
                      // Font held at 11 (originally dropped 12 → 11
                      // on 2026-02-08 to keep 7-figure adjacent-year
                      // values from crowding at 1440 widths).
                      fontSize: font,
                      // 2026-03 (Dave) — Ending Balance values render in
                      // BLACK; Fund Target + Fund Surplus (Deficit) values
                      // render in NAVY (matching their row labels).
                      color: isEnding ? "#000" : (isTargetOrSurplus ? NAVY : undefined),
                      fontWeight: isEnding ? 700 : undefined,
                      textUnderlineOffset: 2,
                      boxShadow: markSides(r.year, peakYear, lowYear, rowIdx === metricRows.length - 1),
                    }}
                    data-testid={
                      isExpenseClickable ? `projection-expense-cell-${r.year}` : undefined
                    }
                    onClick={
                      isExpenseClickable
                        ? (e) => onExpenseClick(r.year, e.currentTarget)
                        : undefined
                    }
                  >
                    {fmtCell({
                      value: r[m.key],
                      balance: m.balance,
                      expense: m.expense,
                      surplus: m.surplus,
                      special: m.special,
                      ramp: isRamp,
                    })}
                  </td>
                );
              })}
              <td
                className="text-right"
                style={{
                  background: bg,
                  padding: 0,
                  // 2026-03 (Dave) — mirror the year-cell rule: Ending
                  // Balance total → BLACK; Fund Target + Fund Surplus
                  // (Deficit) totals → NAVY; every other row inherits.
                  color: isEnding ? "#000" : NAVY,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  // Font shrunk further (11 → 10) so 12-char values
                  // like "($3,857,015)" fit inside the narrower cell
                  // after the 3-px inset margin.
                  fontSize: font,
                }}
              >
                <div
                  style={{
                    padding: `3px ${CELL_PAD}px`,
                  }}
                >
                  {fmtCell({
                    value: totals[m.key],
                    balance: m.balance,
                    expense: m.expense,
                    surplus: m.surplus,
                    special: m.special,
                    isTotal: true,
                  })}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
}

// Empty placeholder shown in place of the bottom panel when horizon ≤ 15.
function EmptyPanelPlaceholder() {
  return (
    <div
      className="w-full flex items-center justify-center text-[11px] text-slate italic"
      style={{
        minHeight: 180,
        border: `1px dashed ${NAVY}22`,
        borderRadius: 8,
        background: "#FAFBFD",
      }}
      data-testid="projections-bottom-empty"
    >
      Years 16–30 will appear here when a 20-, 25-, or 30-year horizon is selected.
    </div>
  );
}

export default function ProjectionsScreen({ calc, variables, horizon, calcTimestamp }) {
  const [expensePopover, setExpensePopover] = useState(null);
  if (!calc) return <div className="text-slate p-4">Loading…</div>;

  const rawRows = calc.projection.slice(0, horizon);

  // Fund Target, Fund Surplus (Deficit) and From Reserves are server-computed (calc.display).
  const targetPctFraction = (variables?.target_pct || 0);
  const targetPctInt = Math.round(targetPctFraction * 100);
  const dYears = calc.display?.years || [];
  const rows = rawRows.map((r, i) => {
    const d = dYears[i] || {};
    return {
      ...r,
      from_reserves: d.from_reserves || 0,
      fund_target: d.target_balance || 0,
      fund_surplus: d.surplus || 0,
    };
  });

  // Dynamic metric-rows list — core 6 rows PLUS Fund Target (labelled
  // with the currently-active target percentage) and Fund Surplus.
  const metricRows = [
    ...CORE_METRIC_ROWS,
    { key: "fund_target",  label: `Fund Target at ${targetPctInt}%`, balance: true },
    { key: "fund_surplus", label: "Fund Surplus (Deficit)",          surplus: true },
  ];

  const topRows = rows.slice(0, SLOTS_PER_PANEL);
  const bottomRows = rows.slice(SLOTS_PER_PANEL);
  const showBottom = bottomRows.length > 0;
  // Peak Year is a defined term for Threshold-contribution math — always
  // the peak within the full 30-year projection, never the currently
  // selected horizon. If the peak falls outside the visible range, the
  // in-table coral outline simply won't render (year isn't drawn), but
  // the callout chip below still names the correct peak year.
  const fullS = calc.display?.horizons?.[String(calc.projection.length)] || {};
  const peakYear = calc.projection.find((r) => r.year === fullS.peak_expense_year) || null;
  const lowBalance = calc.projection.find((r) => r.year === fullS.low_balance_year) || null;

  // Handler wired into every Expenditures cell — opens the drill-down
  // popover anchored just below the clicked cell in VIEWPORT coordinates
  // (the popover is portaled to document.body so it renders above any
  // overflow-hidden ancestor).
  const openExpensePopover = (year, cellEl) => {
    const cellRect = cellEl.getBoundingClientRect();
    setExpensePopover({
      year,
      anchor: {
        x: cellRect.left,
        y: cellRect.bottom + 4,
        flipOffset: cellRect.width + 12,
      },
    });
  };
  const expenseCal = expensePopover
    ? (calc.expenditure_calendar || []).find((c) => c.year === expensePopover.year)
    : null;

  // Top total range:
  //   horizon ≤ 15 → covers only the selected years (e.g. 5-year total)
  //   horizon ≥ 20 → always a fixed 15-year sub-total ("upper panel at a
  //                  full 15 year horizon selection, with a 15 year total")
  const topRange =
    topRows.length ? `${topRows[0].year}\u2013${topRows[topRows.length - 1].year}` : "";
  // Bottom total range — full horizon cumulative pinned on the right.
  const bottomRange = showBottom && rows.length
    ? `${rows[0].year}\u2013${rows[rows.length - 1].year}` : "";

  return (
    <div
      className="panel-compact flex flex-col flex-1 overflow-hidden"
      style={{ marginTop: 11 }}
      data-testid="screen-projections"
    >
      {/* 2026-02-16 (Dave) — the "Reserve Account Matrix" title bar and
          the subtitle beneath it were consuming ~40 px of vertical space
          that pushed the two 15-year blocks below the fold on smaller
          laptops. Both are redundant with the tab label "Projections"
          in the top nav and the visible year headers in the tables
          themselves. Removed entirely to buy back the vertical room. */}

      <div style={{ borderTop: "2px solid #EA6A47", marginBottom: 6 }} data-testid="projections-coral-rule" />
      <div className="overflow-auto flex-1 flex flex-col gap-2">
        {/* 2026-10-08 (Dave) — plan year label on the left of each
            block's blue header. Derived from `rows[0].year` which is
            Y1 of the horizon = the plan year itself. Both blocks of
            the two 15-year windows carry the SAME plan year label. */}
        <YearBlock
          rows={topRows}
          metricRows={metricRows}
          range={topRange}
          rangeSummary={calc.display?.horizons?.[String(topRows.length)]}
          peakYear={peakYear}
          lowYear={lowBalance}
          onExpenseClick={openExpensePopover}
          testid="projections-top"
          planYear={rows[0] ? rows[0].year : undefined}
        />
        {showBottom ? (
          <YearBlock
            rows={bottomRows}
            metricRows={metricRows}
            range={bottomRange}
            rangeSummary={calc.display?.horizons?.[String(rows.length)] /* full horizon cumulative on the bottom-right */}
            peakYear={peakYear}
            lowYear={lowBalance}
            onExpenseClick={openExpensePopover}
            testid="projections-bottom"
            planYear={rows[0] ? rows[0].year : undefined}
          />
        ) : (
          <EmptyPanelPlaceholder />
        )}

        <div className="flex items-center justify-center pb-2 text-[11px] gap-12" style={{ paddingTop: 17 }} data-testid="projections-callouts-row">
          {/* 2026-03 (Dave) — legend now sits inline with the two pills,
              all three centered together on the row. Marker + label
              recoloured navy to match the pill labels; legend itself
              wrapped in the same pill styling as the callouts. */}
          {rows.some((r) => r.is_ramp_year) && (
          <div
            className="flex items-center gap-1.5"
            data-testid="projections-legend"
            style={{
              border: `1px solid ${NAVY}`, borderRadius: 6,
              padding: "4px 10px", background: "#fff",
              fontSize: 11,
            }}
          >
            <span
              className="inline-block"
              style={{
                width: 6, height: 6, borderRadius: 3, background: SKY,
              }}
            />
            <span style={{ color: NAVY }}>Adjusted Contribution</span>
          </div>
          )}

          {peakYear && (
            <div
              className="text-navy"
              data-testid="peak-year-callout"
              style={{
                border: `2px solid ${PEAK_C}`, borderRadius: 6,
                padding: "4px 10px", background: "#fff",
                fontSize: 11,
              }}
            >
              Peak Expenditure Year:{" "}
              <span style={{ fontWeight: 700, color: NAVY }}>
                {peakYear.year}
              </span>
              <span style={{ color: NAVY }}>
                {" · "}{fmtCurrency(Math.abs(Math.round(peakYear.expenditures || 0)))}
              </span>
            </div>
          )}
          {lowBalance && (
            <div
              className="text-navy"
              data-testid="low-balance-callout"
              style={{
                border: `2px solid ${LOW_C}`, borderRadius: 6,
                padding: "4px 10px", background: "#fff",
                fontSize: 11,
              }}
            >
              {/* 2026-02-27 (Dave) — the pill now leads with the YEAR
                  the low-balance point lands in (users kept asking
                  "when does it happen?"), with the dollar amount in
                  parentheses. Mirrors the "Peak Expenditure Year"
                  pill's shape to its left. */}
              Low Balance Year:{" "}
              <span style={{ fontWeight: 700, color: "#D32F2F" }}>
                {lowBalance.year}
              </span>
              <span style={{ color: NAVY }}>
                {" · "}{(lowBalance.ending || 0) < 0 ? "-" : ""}{fmtCurrency(Math.abs(Math.round(lowBalance.ending || 0)))}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Year-total-expense drill-down popover — anchored below the clicked
          expense cell. Portaled to document.body so it never gets clipped
          by ancestor overflow. Dismisses on outside click, Escape, or X. */}
      {expensePopover && expenseCal && (
        <YearExpensesPopover
          year={expensePopover.year}
          total={expenseCal.amount}
          elements={expenseCal.elements}
          anchor={expensePopover.anchor}
          onClose={() => setExpensePopover(null)}
          testIdSuffix="projections"
        />
      )}
    </div>
  );
}
